import type { SpotResultDesktopAttributes } from '@/features/spot-results/spot-results-desktop-v4/spot-results-desktop-v4-type';

import React from 'react';

import useLoadingState from '@/core/components/loading/use-loading-state';
import { Button, Link, MediaSwiper, WishlistButton } from '@/core/features';
import { layoutIdAtom } from '@/core/features/app/app-atoms';
import { useAtomValue } from '@/core/features/store/atom-store';
import { trackSpotResultClick } from '@/features/spot-results/spot-result-tracking';
import SpotResultInfoContainerDesktop from '@/features/spot-results/spot-results-desktop-v4/spot-result-info-container-desktop/spot-result-info-container-desktop';
import { SpotTileTopLivePricesDesktop } from '@/features/spot-results/spot-results-desktop-v4/spot-tile-top-live-prices-desktop/spot-tile-top-live-prices-desktop';
import SpotScoreBadgeDesktop from '@/features/spot-score/spot-score-badge-desktop';

export default function SpotResultDesktop({
    activity,
    activitySpotName,
    badges,
    calendar,
    ctaText,
    destinationTitle,
    id,
    images,
    keyFacts,
    linkUrl,
    spotName,
    spotScore,
    spotTitle,
    topLivePrices,
    wishlistButton,
}: SpotResultDesktopAttributes) {
    const disableClick = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
    };

    const { isLoading, setLoading } = useLoadingState();

    const layoutId = useAtomValue(layoutIdAtom);
    const isMapSearchLayout = layoutId?.includes('Map_Search');

    return (
        <div className={'flex gap-5 w-full min-w-0'}>
            <Link
                display={'flex'}
                fullWidth={true}
                href={linkUrl}
                linkType={'internal'}
                onClick={() => {
                    setLoading(true);
                    return trackSpotResultClick({ activitySpot: activitySpotName, city: spotName, isMap: false });
                }}
                qaId={'qa-spot-page-link-desktop'}
            >
                <div className={'imageContainer relative overflow-hidden'}>
                    <MediaSwiper
                        imageOptions={{
                            height: 300,
                            width: isMapSearchLayout ? 313 : 440,
                        }}
                        items={images}
                        swiperOptions={{
                            debugName: 'SpotResultDesktopV4',
                            loop: true,
                            swiperModuleType: 'gallery',
                        }}
                    />
                    {wishlistButton && (
                        <div className={'wishListContainer flex absolute justify-center items-center z-1'}>
                            <WishlistButton
                                isInitiallySelected={wishlistButton.isSelected}
                                type={'transparent'}
                                wishlistId={id}
                            />
                        </div>
                    )}
                </div>
                <div className={'contentDetails flex flex-col grow p-15 bg-white overflow-hidden'}>
                    <div className={'spotHeaderInfoContainer flex justify-between items-start gap-10 w-full'}>
                        <div>
                            <h2 className={'headerSpotName text-18 font-bold line-clamp-1'}>{spotTitle}</h2>
                            <div
                                className={
                                    'flex flex-wrap w-full gap-10 flex-row text-12 items-center mt-8 line-clamp-2'
                                }
                            >
                                {destinationTitle}
                            </div>
                        </div>
                        <SpotScoreBadgeDesktop
                            fontWeight={'bold'}
                            onClick={disableClick}
                            placement={'left-start'}
                            qaId={'qa-spot-result-score-badge-desktop'}
                            spotScore={spotScore}
                        />
                    </div>

                    <SpotResultInfoContainerDesktop
                        badges={badges}
                        calendar={calendar}
                        keyFacts={keyFacts}
                        onBadgeClick={disableClick}
                    />

                    <div className={'flex justify-end items-end w-full'}>
                        <Button
                            fontSize={16}
                            fontWeight={'bold'}
                            height={40}
                            isLoading={isLoading}
                            title={ctaText}
                            width={200}
                        />
                    </div>
                </div>
            </Link>
            {topLivePrices.length > 0 && (
                <div className={'livePriceContainer shrink-0'}>
                    <SpotTileTopLivePricesDesktop
                        activity={activity}
                        livePrices={topLivePrices}
                        trackingTitle={spotTitle}
                    />
                </div>
            )}
            <style jsx>{`
                .imageContainer {
                    border-radius: 5px 0 0 5px;
                    transition: all 0.8s ease;
                    width: ${isMapSearchLayout ? '230px' : '313px'};
                }

                .contentDetails {
                    border: 1px solid var(--color-alto);
                    border-radius: 0 5px 5px 0;
                    border-left: none;
                    min-height: 302px;
                    width: ${isMapSearchLayout ? 'calc(100% - 230px)' : 'calc(100% - 313px)'};
                }

                .livePriceContainer {
                    width: 122px;
                }

                .spotHeaderInfoContainer {
                    margin-bottom: 12px;
                }

                .headerSpotName:hover {
                    color: var(--color-roseOfSharon);
                }

                .wishListContainer {
                    top: 9px;
                    right: 11px;
                }
            `}</style>
        </div>
    );
}
